import useToggle from 'hooks/CustomHooks/useToggle';
import React, { useEffect, useState } from 'react';
import { PageTitle } from 'styles/Global/commonStyles';
import { HospitalPageLabel, FilterWrapper, DiagnosisInnerLayout, DiagnosisLayout } from 'styles/Global/hospitalStyles';
import TodayDiagnosisFilter from 'pages/DCK/TodayDiagnosisPage/TodayDiagnosisFilter';
import { CL_ALL_HOSPITAL_ID, useGetUserListQueries } from 'api/Queries/CL/User/useGetUserListQueries';
import { useGetFavoriteUserListQueries } from 'api/Queries/CL/User/useGetFavoriteUserListQueries';
import useQueryPaginationStateHooks from 'hooks/CustomHooks/useQueryPaginationStateHooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatUserSearchFilter } from '../utils';
import { pagePaths } from 'constants/path';
import CLManagingPatientsPagination from './CLManagingPatientsPagination';
import { CLManagingPatientSearchFilterList } from '../constants';
import { AccountLevel } from 'utils/statusCode';
import { useLoginStore } from 'store/useLoginDataStore';
import styled from 'styled-components';
import { palette } from 'styles/Global/globalStyles';
import { Body1Bold, Body3Regular } from 'styles/Global/typography';

const CL_USER_MANAGEMENT_TITLE = '사용자 관리';

const CLManagingPatientPage = () => {
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const navigate = useNavigate();
    const { loginData } = useLoginStore();

    const [selectedSearchFilter, setselectedSearchFilter] = useState<number>(0);
    const [searchValue, setSearchValue] = useState<string>(queryParams.get('search') || '');
    const [sortDirections, setSortDirections] = useState<Record<string, boolean>>({ registrationTimestamp: false });
    const [favoriteSortDirections, setFavoriteSortDirections] = useState<Record<string, boolean>>({ registrationTimestamp: false });
    // const [sortField, setSortField] = useState<string>('regNum');
    const [sortField, setSortField] = useState<string>('registrationTimestamp');
    const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
    const [selectedFavoritePatients, setSelectedFavoritePatients] = useState<string[]>([]);

    const [isSearchFilterDropdown, setIsSearchFilterDropdown] = useToggle();

    const { currentPage, pageGroup, setPageGroup, setMultipleParams } = useQueryPaginationStateHooks();
    const isAdminAccount = loginData.level === AccountLevel.admin;
    const managedHospitalId = isAdminAccount ? CL_ALL_HOSPITAL_ID : undefined;

    const {
        data: userListData,
        refetch: refetchUserListData,
        isLoading: loading,
    } = useGetUserListQueries(
        currentPage,
        pageGroup,
        formatUserSearchFilter(CLManagingPatientSearchFilterList[selectedSearchFilter]),
        queryParams.get('search') || '',
        sortField,
        sortDirections[sortField] ? 'asc' : 'desc',
        isAdminAccount ? null : false,
        managedHospitalId
    );

    const {
        data: favoriteUserListData,
        refetch: refetchFavoriteUserListData,
        isLoading: favoriteLoading,
    } = useGetFavoriteUserListQueries('registrationTimestamp', favoriteSortDirections[sortField] ? 'asc' : 'desc', !isAdminAccount);

    // 브라우저 새로고침 감지
    useEffect(() => {
        const navigation = window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

        if (navigation.type === 'reload') {
            // 새로고침 시 기본 경로로 이동
            navigate(pagePaths.CLAdminManagePatient, { replace: true });
        }
    }, []);

    useEffect(() => {
        setSearchValue(queryParams.get('search') || '');
    }, [search]);

    // search dropdown function
    const handleDropdown = (e: React.MouseEvent, idx: number) => {
        e.stopPropagation();
        setIsSearchFilterDropdown();
        setselectedSearchFilter(idx);
        setSearchValue('');
    };

    // SearchFilter function
    const handleSearchFilter = () => {
        handleClear();
        setMultipleParams({
            page: 1,
            pageGroup: 0,
            search: searchValue,
        });
        refetchUserListData();
    };

    // reset Button function
    const handleResetButton = () => {
        navigate(pagePaths.CLAdminManagePatient, { replace: true });
    };

    // TODO: 추후 pagination 다른 component에 같은 형식으로 수정 필요
    const handlePageChange = (page: number, newGroup?: number) => {
        setMultipleParams(
            {
                page: page,
                pageGroup: newGroup !== undefined ? newGroup : Number(queryParams.get('pageGroup')) || 0,
                // selectedSearchFilter: selectedSearchFilter,
                search: searchValue,
            },
            page,
            newGroup !== undefined ? newGroup : Number(queryParams.get('pageGroup')) || 0
        );
    };

    // sorting function
    const handleSorting = (type: string) => {
        if (type === 'registrationTimestamp') {
            const direction = !sortDirections['registrationTimestamp'];
            setSortField('registrationTimestamp');
            setSortDirections({ registrationTimestamp: direction });
        }
    };

    const handleFavoriteSorting = (type: string) => {
        if (type === 'registrationTimestamp') {
            const direction = !favoriteSortDirections['registrationTimestamp'];
            setFavoriteSortDirections({ registrationTimestamp: direction });
            refetchFavoriteUserListData();
        }
    };

    const handleClear = () => {
        setSortField('registrationTimestamp');
        setSortDirections({ registrationTimestamp: true });
    };

    const handlePatientSelect = (patientId: string) => {
        setSelectedPatients((prev) => {
            if (prev.includes(patientId)) {
                return prev.filter((id) => id !== patientId);
            }
            return [...prev, patientId];
        });
    };

    const handleFavoritePatientSelect = (patientId: string) => {
        setSelectedFavoritePatients((prev) => {
            if (prev.includes(patientId)) {
                return prev.filter((id) => id !== patientId);
            }
            return [...prev, patientId];
        });
    };

    const isWaitingFavoriteData = !isAdminAccount && (!favoriteUserListData || favoriteLoading);

    return (
        <DiagnosisLayout>
            {!userListData || loading || isWaitingFavoriteData ? null : (
                <DiagnosisInnerLayout>
                    <HospitalPageLabel onClick={() => window.location.reload()}>{CL_USER_MANAGEMENT_TITLE}</HospitalPageLabel>
                    <PageTitle $marginbottom='24px'>{CL_USER_MANAGEMENT_TITLE}</PageTitle>
                    {!isAdminAccount && (
                        <FavoriteLayoutWrapper>
                            <Divider />
                            <CountTextWrapper>
                                <Body1Bold>오늘 진료</Body1Bold>
                                <Body3Regular>{favoriteUserListData?.list.length || 0}명의 환자</Body3Regular>
                            </CountTextWrapper>
                            <CLManagingPatientsPagination
                                type='clFavoritePatients'
                                initNumber={favoriteUserListData?.list.length}
                                currentPage={currentPage}
                                totalPages={favoriteUserListData?.pagination?.totalPages}
                                pageGroup={pageGroup}
                                handlePageGroup={setPageGroup}
                                handlePageChange={handlePageChange}
                                currentData={favoriteUserListData?.list}
                                handleSorting={handleFavoriteSorting}
                                sortField={sortField}
                                sortDirections={favoriteSortDirections}
                                handleResetButton={handleResetButton}
                                showPagination={false}
                                selectedPatients={selectedFavoritePatients}
                                onPatientSelect={handleFavoritePatientSelect}
                                showFavoriteButton={true}
                                refetchUserListData={refetchUserListData}
                                refetchFavoriteUserListData={refetchFavoriteUserListData}
                            />
                        </FavoriteLayoutWrapper>
                    )}
                    <FilterWrapper>
                        <TodayDiagnosisFilter
                            type='clManagingPatients'
                            diagnosisNumber={userListData?.pagination?.totalCount}
                            selectedSearchFilter={selectedSearchFilter}
                            handleDropdown={handleDropdown}
                            isSearchFilterDropdown={isSearchFilterDropdown}
                            setIsSearchFilterDropdown={setIsSearchFilterDropdown}
                            onChange={setSearchValue}
                            searchValue={searchValue}
                            handleSearchFilter={handleSearchFilter}
                        />
                    </FilterWrapper>
                    <CLManagingPatientsPagination
                        type='clManagingPatients'
                        initNumber={userListData?.list?.length}
                        currentPage={currentPage}
                        totalPages={userListData?.pagination?.totalPages}
                        pageGroup={pageGroup}
                        handlePageGroup={setPageGroup}
                        handlePageChange={handlePageChange}
                        currentData={userListData?.list}
                        handleSorting={handleSorting}
                        sortField={sortField}
                        sortDirections={sortDirections}
                        handleResetButton={handleResetButton}
                        showPagination={true}
                        selectedPatients={selectedPatients}
                        onPatientSelect={handlePatientSelect}
                        showFavoriteButton={!isAdminAccount}
                        refetchUserListData={refetchUserListData}
                        refetchFavoriteUserListData={refetchFavoriteUserListData}
                    />
                </DiagnosisInnerLayout>
            )}
        </DiagnosisLayout>
    );
};

export default CLManagingPatientPage;

const Divider = styled.div`
    width: 100%;
    height: 1px;
    background-color: ${palette.testGray};
    margin-bottom: 16px;
`;

const FavoriteLayoutWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 22px;
    padding-bottom: 48px;
`;

const CountTextWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;
